import { useState, type ReactNode } from 'react';
import { InformationCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';

/**
 * ModuleIntro — a consistent, dismissible "What is this?" orientation banner for the top
 * of a module page. First-time users get a plain-language explanation of what the page is,
 * where its data comes from, and what the key actions do; repeat users collapse it to an
 * unobtrusive "What is this?" link they can re-open any time.
 *
 * Frictionless by design (see feedback_ux_friction_priority): the dismissed state persists
 * per-module in localStorage, so we orient newcomers without nagging everyone else.
 *
 * Usage:
 *   <ModuleIntro storageKey="enforcement-intelligence" title="What is Enforcement Intelligence?">
 *     <p>…plain-language explanation…</p>
 *     <ModuleIntro.Points items={['Where the data comes from', 'What Analyze does']} />
 *   </ModuleIntro>
 */

interface ModuleIntroProps {
  /** Unique key per module — drives the localStorage "dismissed" flag. */
  storageKey: string;
  title: string;
  children: ReactNode;
}

const LS_PREFIX = 'clarid:module-intro-dismissed:';

function ModuleIntro({ storageKey, title, children }: ModuleIntroProps) {
  const lsKey = `${LS_PREFIX}${storageKey}`;
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(lsKey) === '1';
    } catch {
      return false;
    }
  });

  const setDismissedPersisted = (value: boolean) => {
    setDismissed(value);
    try {
      if (value) localStorage.setItem(lsKey, '1');
      else localStorage.removeItem(lsKey);
    } catch {
      /* storage unavailable (private mode) — fall back to in-memory only */
    }
  };

  if (dismissed) {
    return (
      <button
        type="button"
        onClick={() => setDismissedPersisted(false)}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-teal-700 hover:text-teal-800"
      >
        <InformationCircleIcon className="h-4 w-4" />
        What is this?
      </button>
    );
  }

  return (
    <div className="relative rounded-xl border border-teal-100 bg-teal-50/60 p-4 pr-10">
      <button
        type="button"
        onClick={() => setDismissedPersisted(true)}
        aria-label="Dismiss explanation"
        className="absolute right-3 top-3 rounded-md p-1 text-teal-500 hover:bg-teal-100 hover:text-teal-700"
      >
        <XMarkIcon className="h-4 w-4" />
      </button>
      <div className="flex gap-3">
        <InformationCircleIcon className="h-5 w-5 flex-shrink-0 text-teal-600" />
        <div className="space-y-2 text-sm leading-relaxed text-gray-700">
          <p className="font-semibold text-gray-900">{title}</p>
          {children}
        </div>
      </div>
    </div>
  );
}

/** A tidy bulleted list for "here's what each part does" style points. */
function Points({ items }: { items: ReactNode[] }) {
  return (
    <ul className="ml-1 space-y-1">
      {items.map((item, i) => (
        <li key={i} className="flex gap-2">
          <span className="mt-1.5 h-1 w-1 flex-shrink-0 rounded-full bg-teal-500" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

ModuleIntro.Points = Points;

export default ModuleIntro;
